import type { KryspaceRenderManifest } from '../../../types/kryspaceRenderManifest';
import type { KryspaceControlBridgeStructure } from '../../../types/kryspaceControlBridgeStructure';
import type { ControlBridgeQueueItem, ControlBridgeViewModel } from '../deriveViewModel';
import type { ResourceFilter, ScopeContext } from '../filters';
import { passesFilter } from '../filters';
import { sortByPriority } from '../queueHelpers';
import { scopeDriveHiFiModel } from './driveScope';
import { mapPersonalLayerFromManifest } from './mapFromManifestPersonalLayer';
import { mapDriveHiFiModel, mapGuidanceHiFiModel, type PersonalLayerMapContext } from './mapFromControlBridge';
import type { DriveHiFiModel, GuidanceHiFiModel, PersonalOverlayMode } from './types';

export interface PersonalLayerUserContext {
  id?: string;
  label?: string;
  username?: string;
  email?: string;
  resourceProfileRef?: string;
}

export interface BuildPersonalLayerInput {
  vm: ControlBridgeViewModel;
  structure: KryspaceControlBridgeStructure;
  manifest?: KryspaceRenderManifest;
  scope: string;
  resource: ResourceFilter;
  scopeContext?: ScopeContext;
  overlayMode?: PersonalOverlayMode;
  user?: PersonalLayerUserContext;
}

export interface PersonalLayerViewModels {
  guidance: GuidanceHiFiModel | null;
  drive: DriveHiFiModel;
  overlayMode: PersonalOverlayMode;
  source: 'manifest' | 'proxy';
}

function filterQueue(
  items: ControlBridgeQueueItem[],
  scope: string,
  resource: ResourceFilter,
  companyWide: boolean,
  scopeContext?: ScopeContext
): ControlBridgeQueueItem[] {
  const scoped = companyWide
    ? items
    : items.filter((item) => passesFilter(item, scope, resource, scopeContext));
  return sortByPriority(
    scoped.filter((item) => passesFilter(item, companyWide ? '__all' : scope, resource, scopeContext))
  );
}

export function buildPersonalLayerViewModels(input: BuildPersonalLayerInput): PersonalLayerViewModels {
  const overlayMode = input.overlayMode ?? 'system';

  if (input.manifest) {
    const fromManifest = mapPersonalLayerFromManifest({
      manifest: input.manifest,
      scope: input.scope,
      resource: input.resource,
      scopeContext: input.scopeContext,
      userRef: input.user?.id,
      userLabel: input.user?.label,
    });
    if (fromManifest && (fromManifest.guidance || fromManifest.drive)) {
      const companyWide = !input.scope || input.scope === 'company';
      const eligibleWork = filterQueue(
        input.vm.eligibleWork,
        input.scope,
        input.resource,
        companyWide,
        input.scopeContext
      );
      const openIssues = filterQueue(
        input.vm.openIssues,
        input.scope,
        input.resource,
        companyWide,
        input.scopeContext
      );
      const ctx: PersonalLayerMapContext = {
        scope: input.scope,
        eligibleWork,
        openIssues,
        currentUserRef: input.user?.id,
        currentUserLabel: input.user?.label,
      };
      const rawDrive =
        fromManifest.drive && fromManifest.drive.subdomains.length > 0
          ? fromManifest.drive
          : mapDriveHiFiModel(input.structure, input.vm, ctx);
      return {
        guidance: fromManifest.guidance
          ? { ...fromManifest.guidance, eligibleWork }
          : mapGuidanceHiFiModel(input.vm, ctx),
        drive: scopeDriveHiFiModel({
          drive: rawDrive,
          structure: input.structure,
          scope: input.scope,
          resource: input.resource,
          scopeContext: input.scopeContext,
          openIssues,
          eligibleWork,
        }),
        overlayMode,
        source: 'manifest',
      };
    }
  }

  const companyWide = !input.scope || input.scope === 'company';
  const eligibleWork = filterQueue(
    input.vm.eligibleWork,
    input.scope,
    input.resource,
    companyWide,
    input.scopeContext
  );
  const openIssues = filterQueue(
    input.vm.openIssues,
    input.scope,
    input.resource,
    companyWide,
    input.scopeContext
  );

  const ctx: PersonalLayerMapContext = {
    scope: input.scope,
    eligibleWork,
    openIssues,
    currentUserRef: input.user?.id,
    currentUserLabel: input.user?.label,
  };

  return {
    guidance: mapGuidanceHiFiModel(input.vm, ctx),
    drive: scopeDriveHiFiModel({
      drive: mapDriveHiFiModel(input.structure, input.vm, ctx),
      structure: input.structure,
      scope: input.scope,
      resource: input.resource,
      scopeContext: input.scopeContext,
      openIssues,
      eligibleWork,
    }),
    overlayMode,
    source: 'proxy',
  };
}
